import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { StripeFacade } from '@app/store/stripe';
import { UserSubscription } from '@app/core/api/stripe';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-low-minutes-toast',
  templateUrl: './low-minutes-toast.component.html',
  styleUrl: './low-minutes-toast.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    TranslateModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LowMinutesToastComponent implements OnInit {
  // Toast visibility state
  isVisible = signal(false);

  // Subscription data
  subscription$: Observable<UserSubscription | null>;

  constructor(
    private stripeFacade: StripeFacade,
    private router: Router
  ) {
    // Use the new currentSubscription$ selector from facade
    // This automatically merges base subscription with real-time session data
    this.subscription$ = this.stripeFacade.currentSubscription$;
  }

  ngOnInit(): void {
    // Subscribe to subscription changes to control visibility
    this.subscription$
      .pipe(untilDestroyed(this))
      .subscribe(subscription => {
        this.updateVisibility(subscription);
      });
  }

  private updateVisibility(subscription: UserSubscription | null): void {
    if (!subscription) {
      this.isVisible.set(false);
      return;
    }

    // Show toast when user has ≤ 5 minutes remaining
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    const shouldShow = this.stripeFacade.shouldShowLowMinutesWarning(subscription, remaining);
    this.isVisible.set(shouldShow);
  }

  shouldShowLowMinutesWarning(subscription: UserSubscription): boolean {
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    return this.stripeFacade.shouldShowLowMinutesWarning(subscription, remaining);
  }

  getRemainingMinutes(subscription: UserSubscription): number {
    return this.stripeFacade.getRemainingMinutes(subscription);
  }

  onToastClick(event: Event): void {
    // Stop propagation to prevent parent container click handler from triggering
    // (which would navigate back to /home in minimized mode)
    event.stopPropagation();
    this.router.navigate(['/billing']);
  }
}
